/**
 * SelectionRequestBuilder Component
 * Staff UI for managing selection requests and viewing client submissions
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  FiSend,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiClock,
  FiEye,
  FiLock,
  FiUnlock,
  FiImage,
  FiLink,
  FiCopy,
  FiExternalLink,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiCheckCircle,
  FiMail
} from 'react-icons/fi';
import { buildVariantUrl } from '../utils/photoCache';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'gray', icon: FiEdit2 },
  sent: { label: 'Sent', color: 'blue', icon: FiSend },
  opened: { label: 'Opened', color: 'yellow', icon: FiEye },
  submitted: { label: 'Submitted', color: 'green', icon: FiCheckCircle },
  locked: { label: 'Locked', color: 'purple', icon: FiLock },
  cancelled: { label: 'Cancelled', color: 'red', icon: FiX }
};

function formatDate(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatDateTime(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Selection request card with expandable submissions
function SelectionRequestCard({ request, onSend, onLock, onCancel, onDelete, onViewSubmission }) {
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;

  const handleSend = async () => {
    setSending(true);
    const result = await onSend(request);
    if (result?.token) {
      setShareToken(result.token);
    }
    setSending(false);
  };

  const copyLink = () => {
    const url = `${window.location.origin}/s/${shareToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const rules = request.rules || {};
  const hasSubmissions = request.submissions && request.submissions.length > 0;

  return (
    <div className={`selection-request-card status-${request.status}`}>
      <div className="request-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="request-card-title">
          <h4>{request.title}</h4>
          <span className={`status-badge status-${status.color}`}>
            <StatusIcon size={12} />
            {status.label}
          </span>
        </div>
        <button className="expand-btn">
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>

      <div className="request-card-meta">
        <span><FiImage size={14} /> {request.gallery_title} ({request.image_count || 0} photos)</span>
        {request.due_at && (
          <span className={new Date(request.due_at) < new Date() ? 'overdue' : ''}>
            <FiClock size={14} /> Due {formatDate(request.due_at)}
          </span>
        )}
        {hasSubmissions && (
          <span className="has-submissions">
            <FiCheckCircle size={14} /> {request.submissions.length} submission(s)
          </span>
        )}
      </div>

      {(rules.minSelections || rules.maxSelections) && (
        <div className="request-rules">
          {rules.minSelections && <span>Min: {rules.minSelections}</span>}
          {rules.maxSelections && <span>Max: {rules.maxSelections}</span>}
          {rules.allowRanking && <span>Ranking enabled</span>}
          {rules.allowNotesPerAsset && <span>Notes enabled</span>}
        </div>
      )}

      {request.instructions && (
        <p className="request-instructions">{request.instructions}</p>
      )}

      {/* Share token display */}
      {shareToken && (
        <div className="token-created-box">
          <div className="token-success">
            <FiCheck size={16} /> Link created!
          </div>
          <div className="token-url-row">
            <input type="text" value={`${window.location.origin}/s/${shareToken}`} readOnly />
            <button onClick={copyLink} className="btn-copy">
              {copied ? <FiCheck /> : <FiCopy />}
            </button>
          </div>
        </div>
      )}

      {/* Actions based on status */}
      <div className="request-card-actions">
        {request.status === 'draft' && (
          <>
            <button onClick={handleSend} className="client-action-btn primary" disabled={sending}>
              <FiSend /> {sending ? 'Sending...' : 'Send to Client'}
            </button>
            <button onClick={() => onDelete(request)} className="client-action-btn danger">
              <FiTrash2 />
            </button>
          </>
        )}

        {['sent', 'opened'].includes(request.status) && (
          <>
            <button onClick={() => onCancel(request)} className="client-action-btn warning">
              <FiX /> Cancel
            </button>
          </>
        )}

        {request.status === 'submitted' && (
          <>
            <button onClick={() => onLock(request)} className="client-action-btn">
              <FiLock /> Lock Selection
            </button>
          </>
        )}

        {request.status === 'locked' && (
          <span className="locked-notice">
            <FiLock size={14} /> Selection is locked
          </span>
        )}
      </div>

      {/* Expanded view with submissions */}
      {expanded && hasSubmissions && (
        <div className="request-submissions">
          <h5>Submissions</h5>
          {request.submissions.map(submission => (
            <div key={submission.id} className="submission-row">
              <div className="submission-info">
                <span className="submission-count">
                  <FiImage size={14} /> {submission.selection_count} selections
                </span>
                <span className="submission-date">
                  Submitted {formatDateTime(submission.submitted_at)}
                </span>
                {submission.revision > 1 && (
                  <span className="submission-revision">Revision {submission.revision}</span>
                )}
                {submission.locked && (
                  <span className="submission-locked"><FiLock size={12} /> Locked</span>
                )}
              </div>
              <div className="submission-actions">
                <button onClick={() => onViewSubmission(submission)} className="client-action-btn small">
                  <FiEye /> View
                </button>
                <a
                  href={`/api/selection-requests/${request.id}/submissions/${submission.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="client-action-btn small"
                >
                  <FiDownload /> Export
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      {expanded && (
        <div className="request-timeline">
          <h5>Timeline</h5>
          <div className="timeline-items">
            <div className="timeline-item">
              <span className="timeline-dot" />
              <span>Created {formatDateTime(request.created_at)}</span>
            </div>
            {request.sent_at && (
              <div className="timeline-item">
                <span className="timeline-dot sent" />
                <span>Sent {formatDateTime(request.sent_at)}</span>
              </div>
            )}
            {request.opened_at && (
              <div className="timeline-item">
                <span className="timeline-dot opened" />
                <span>Opened {formatDateTime(request.opened_at)}</span>
              </div>
            )}
            {request.submitted_at && (
              <div className="timeline-item">
                <span className="timeline-dot submitted" />
                <span>Submitted {formatDateTime(request.submitted_at)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Submission viewer modal
function SubmissionViewerModal({ submission, onClose }) {
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    // Load thumbnail URLs for selected images
    if (submission?.selections) {
      loadImageUrls(submission.selections);
    }
  }, [submission]);

  const loadImageUrls = async (selections) => {
    const urls = {};
    for (const sel of selections) {
      if (sel.filename) {
        urls[sel.assetId] = buildVariantUrl({ filename: sel.filename }, 'xl');
      }
    }
    setImageUrls(urls);
  };

  if (!submission) return null;

  const selections = submission.selections || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel submission-viewer-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Selection Submission</h3>
          <button onClick={onClose} className="modal-close">
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          <div className="submission-meta">
            <span>{selections.length} selections</span>
            <span>Submitted {formatDateTime(submission.submitted_at)}</span>
            {submission.revision > 1 && <span>Revision {submission.revision}</span>}
          </div>

          <div className="submission-grid">
            {selections.map((sel, index) => (
              <div key={sel.assetId} className="submission-item">
                <div className="submission-thumb">
                  {imageUrls[sel.assetId] ? (
                    <img src={imageUrls[sel.assetId]} alt={`Selection ${index + 1}`} />
                  ) : (
                    <div className="thumb-placeholder">
                      <FiImage />
                    </div>
                  )}
                  <span className="selection-rank">{sel.rank || index + 1}</span>
                </div>
                {sel.note && (
                  <p className="selection-note">{sel.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
          <a
            href={`/api/selection-requests/submissions/${submission.id}/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            <FiDownload /> Export Selection
          </a>
        </div>
      </div>
    </div>
  );
}

// Main SelectionRequestBuilder component
export default function SelectionRequestBuilder({ jobId, customerId, galleries, onRequestChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingSubmission, setViewingSubmission] = useState(null);

  useEffect(() => {
    loadRequests();
  }, [jobId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`/api/selection-requests?job_id=${jobId}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to load selection requests');

      const data = await res.json();
      setRequests(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (request) => {
    try {
      const res = await fetch(`/api/selection-requests/${request.id}/send`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to send selection request');

      const data = await res.json();
      await loadRequests();
      onRequestChange?.();
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const handleLock = async (request) => {
    if (!confirm('Lock this selection? The client will no longer be able to make changes.')) return;

    try {
      const res = await fetch(`/api/selection-requests/${request.id}/lock`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to lock selection');

      await loadRequests();
      onRequestChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = async (request) => {
    if (!confirm('Cancel this selection request?')) return;

    try {
      const res = await fetch(`/api/selection-requests/${request.id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to cancel selection');

      await loadRequests();
      onRequestChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (request) => {
    if (!confirm(`Delete "${request.title}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`/api/selection-requests/${request.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete selection request');

      await loadRequests();
      onRequestChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  // Group requests by status
  const groupedRequests = useMemo(() => {
    const groups = {
      active: [],
      completed: [],
      other: []
    };

    requests.forEach(req => {
      if (['draft', 'sent', 'opened'].includes(req.status)) {
        groups.active.push(req);
      } else if (['submitted', 'locked'].includes(req.status)) {
        groups.completed.push(req);
      } else {
        groups.other.push(req);
      }
    });

    return groups;
  }, [requests]);

  if (loading) {
    return (
      <div className="selection-builder loading">
        <div className="spinner" />
        <span>Loading selection requests...</span>
      </div>
    );
  }

  return (
    <div className="selection-builder">
      {error && (
        <div className="selection-error">
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}><FiX /></button>
        </div>
      )}

      <div className="selection-builder-header">
        <div>
          <h3>Selection Requests</h3>
          <p>Manage photo selection workflows with your client</p>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="selection-empty">
          <FiSend size={48} />
          <h4>No selection requests yet</h4>
          <p>Create a selection request from a gallery to let your client choose their favorites</p>
        </div>
      ) : (
        <>
          {/* Active requests */}
          {groupedRequests.active.length > 0 && (
            <div className="request-group">
              <h4 className="group-title">
                <span className="dot active" />
                Active Requests
              </h4>
              <div className="request-list">
                {groupedRequests.active.map(request => (
                  <SelectionRequestCard
                    key={request.id}
                    request={request}
                    onSend={handleSend}
                    onLock={handleLock}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    onViewSubmission={setViewingSubmission}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Completed requests */}
          {groupedRequests.completed.length > 0 && (
            <div className="request-group">
              <h4 className="group-title">
                <span className="dot completed" />
                Completed
              </h4>
              <div className="request-list">
                {groupedRequests.completed.map(request => (
                  <SelectionRequestCard
                    key={request.id}
                    request={request}
                    onSend={handleSend}
                    onLock={handleLock}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    onViewSubmission={setViewingSubmission}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Cancelled requests */}
          {groupedRequests.other.length > 0 && (
            <div className="request-group">
              <h4 className="group-title">
                <span className="dot other" />
                Cancelled
              </h4>
              <div className="request-list">
                {groupedRequests.other.map(request => (
                  <SelectionRequestCard
                    key={request.id}
                    request={request}
                    onSend={handleSend}
                    onLock={handleLock}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    onViewSubmission={setViewingSubmission}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Submission viewer modal */}
      {viewingSubmission && (
        <SubmissionViewerModal
          submission={viewingSubmission}
          onClose={() => setViewingSubmission(null)}
        />
      )}
    </div>
  );
}
